import React, { useState, useEffect, useRef } from 'react';
import ChatService from '../services/ChatService';
import ChatRoomListItem from '../components/ChatRoomListItem';
import soundNotification from '../utils/soundNotification';
import browserNotifications from '../utils/browserNotifications';
import { theme } from '../styles/theme';
import './ChatListScreen.css';

/**
 * ChatListScreen - Main chat list component
 * Matches Flutter ChatListScreen design
 */
const ChatListScreen = ({ 
  currentUserId, 
  currentUserName, 
  currentUserRole, 
  currentAgencyId,
  onChatRoomClick,
  onCreateChat,
  onlineUsers: sharedOnlineUsers,
  setOnlineUsers: setSharedOnlineUsers
}) => {
  const [chatRooms, setChatRooms] = useState([]);
  const [filteredChatRooms, setFilteredChatRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  // Use shared onlineUsers if provided, otherwise fallback to local state
  const [localOnlineUsers, setLocalOnlineUsers] = useState(new Set());
  const onlineUsers = sharedOnlineUsers || localOnlineUsers;
  const setOnlineUsers = setSharedOnlineUsers || setLocalOnlineUsers;
  const [isSearching, setIsSearching] = useState(false);
  const [apiResponse, setApiResponse] = useState(null); // Store API response for debugging
  const [apiUrlCalled, setApiUrlCalled] = useState('');
  
  // Store listener references for cleanup
  const globalMessageListenerRef = useRef(null);
  const roomUpdateListenerRef = useRef(null);
  const onlineStatusListenerRef = useRef(null);
  const chatListContainerRef = useRef(null);
  const isUserScrollingListRef = useRef(false);
  const listScrollTimeoutRef = useRef(null);
  const loadChatRoomsRef = useRef(null); // Ref so parent can trigger refresh (e.g. after creating a room)

  // Compute userId for chat (formatted admin ID for admin users)
  const userIdForChat = currentUserRole === 'admin' 
    ? `admin_${currentAgencyId}` 
    : currentUserId;

  useEffect(() => {
    initializeChat();

    // Initialize audio on first user interaction (required by browsers)
    const initializeAudio = () => {
      soundNotification.initializeOnUserInteraction();
      // Remove listener after first interaction
      document.removeEventListener('click', initializeAudio);
      document.removeEventListener('touchstart', initializeAudio);
      document.removeEventListener('keydown', initializeAudio);
    };
    
    // Listen for first user interaction to enable audio
    document.addEventListener('click', initializeAudio, { once: true });
    document.addEventListener('touchstart', initializeAudio, { once: true });
    document.addEventListener('keydown', initializeAudio, { once: true });

    // Periodic refresh every 10 seconds
    const refreshInterval = setInterval(() => {
      if (!isLoading) {
        loadChatRooms();
      }
    }, 10000);

    return () => {
      clearInterval(refreshInterval);
      // Properly cleanup listeners
      if (globalMessageListenerRef.current) {
        ChatService.removeGlobalMessageListener(globalMessageListenerRef.current);
      }
      if (roomUpdateListenerRef.current) {
        ChatService.removeRoomUpdateListener(roomUpdateListenerRef.current);
      }
      if (onlineStatusListenerRef.current) {
        ChatService.removeOnlineStatusListener(onlineStatusListenerRef.current);
      }
      if (listScrollTimeoutRef.current) {
        clearTimeout(listScrollTimeoutRef.current);
      }
    };
  }, []);

  // Expose refresh so parent (Chat.jsx) can refresh list when user creates a new room
  useEffect(() => {
    window.__refreshChatList = () => loadChatRoomsRef.current?.();
    return () => {
      window.__refreshChatList = null;
    };
  }, []);

  // Setup scroll detection for chat list to prevent jerking (separate effect for when container is available)
  useEffect(() => {
    const container = chatListContainerRef.current;
    if (!container) return;
    
    const handleListScroll = () => {
      isUserScrollingListRef.current = true;
      
      // Reset flag after user stops scrolling
      if (listScrollTimeoutRef.current) {
        clearTimeout(listScrollTimeoutRef.current);
      }
      listScrollTimeoutRef.current = setTimeout(() => {
        isUserScrollingListRef.current = false;
      }, 500);
    };
    
    container.addEventListener('scroll', handleListScroll, { passive: true });
    container.addEventListener('wheel', handleListScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleListScroll);
      container.removeEventListener('wheel', handleListScroll);
      if (listScrollTimeoutRef.current) {
        clearTimeout(listScrollTimeoutRef.current);
      }
    };
  }, [chatRooms.length]); // Re-run when chat rooms change (container might re-render)

  useEffect(() => {
    filterChatRooms();
  }, [searchQuery, chatRooms]);

  const initializeChat = async () => {
    try {
      // Construct admin ID if needed
      let userIdForChat = currentUserId;
      if (currentUserRole === 'admin') {
        userIdForChat = `admin_${currentAgencyId}`;
      }

      // Connect to socket
      ChatService.connect({
        userId: userIdForChat,
        userName: currentUserName,
        userRole: currentUserRole,
        agencyId: currentAgencyId,
      });

      // Listen for online/offline status - matches Flutter implementation
      const onlineStatusCallback = (data) => {
        const userId = data.userId || data.user_id;
        const online = data.online === true || data.online === 1 || data.online === 'true';
        
        if (!userId) return;
        
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          const userIdStr = String(userId);
          
          // Remove any existing entry (handles type mismatches - string vs number)
          newSet.forEach(id => {
            if (String(id) === userIdStr) {
              newSet.delete(id);
            }
          });
          
          if (online) {
            // Add the userId (keep original type for consistency)
            newSet.add(userId);
          }
          // If offline, we've already removed it above
          
          return newSet;
        });
      };
      ChatService.onOnlineStatus(onlineStatusCallback);
      onlineStatusListenerRef.current = onlineStatusCallback;
      
      // Request online users on connection (matches Flutter)
      ChatService.requestOnlineUsers();
      
      // Also request after a short delay to catch any missed events
      setTimeout(() => {
        ChatService.requestOnlineUsers();
      }, 500);

      // Listen for real-time message updates
      const globalMessageCallback = (message) => {
        // Only play sound if:
        // 1. Message is not from current user
        // 2. Window/tab is visible (user is on the page)
        // Sound will play for all new messages in list view (user can see which room has new message)
        const isPageVisible = !document.hidden;
        const isMessageFromOtherUser = message.senderId !== userIdForChat;
        
        if (isPageVisible && isMessageFromOtherUser) {
          // Play notification sound for new message in chat list
          soundNotification.playNotification().catch(err => {
            console.warn('⚠️ Could not play notification sound:', err);
          });
        }
        
        updateChatListWithNewMessage(message);

        // Show browser notification if page is not visible and message is from another user
        if (!browserNotifications.isPageVisible() && message.senderId !== userIdForChat) {
          browserNotifications.showNotification(message.senderName || 'New Message', {
            body: message.message || (message.fileName ? `Sent ${message.fileName}` : 'New message'),
            tag: message.roomId,
            onClick: () => {
              // Focus window when notification clicked
              window.focus();
              // Optionally navigate to chat room
              if (onChatRoomClick) {
                const room = chatRooms.find(r => r.roomId === message.roomId);
                if (room) {
                  onChatRoomClick(room);
                }
              }
            }
          });
        }
      };
      ChatService.onGlobalMessage(globalMessageCallback);
      globalMessageListenerRef.current = globalMessageCallback;

      // Listen for room updates
      const roomUpdateCallback = (data) => {
        handleRoomUpdate(data);
      };
      ChatService.onRoomUpdate(roomUpdateCallback);
      roomUpdateListenerRef.current = roomUpdateCallback;

      // Load chat rooms
      await loadChatRooms();
    } catch (error) {
      console.error('Error initializing chat:', error);
      setIsLoading(false);
    }
  };

  // Helper function to filter out office admin-to-office admin rooms for officeAdmin users
  // CRITICAL: Office Admin users should NEVER see office admin-to-office admin chats in the list
  const filterOfficeAdminToOfficeAdminRooms = (rooms) => {
    if (currentUserRole !== 'officeAdmin') {
      return rooms; // No filtering needed for admin or driver
    }
    
    const roomsBeforeFilter = rooms.length;
    const filtered = rooms.filter(room => {
      const participants = room.participants || [];
      
      // Check if this room is office admin-to-office admin (both participants are officeAdmin)
      const officeAdminCount = participants.filter(p => {
        const role = p.userRole || p.role || '';
        return role === 'officeAdmin' || role === 'office Admin';
      }).length;
      
      const isOfficeAdminToOfficeAdmin = officeAdminCount === 2;
      
      if (isOfficeAdminToOfficeAdmin) {
        return false; // Exclude this room
      }
      return true; // Keep this room (has driver or admin)
    });
    
    const roomsAfterFilter = filtered.length;
    
    return filtered;
  };

  const loadChatRooms = async () => {
    try {
      // Construct admin ID if needed - EXACTLY like Flutter code
      // Flutter code: if (_currentUserRole == 'admin') { userIdForChat = 'admin_${_currentAgencyId}'; }
      // For officeAdmin and driver, use the actual userId directly
      let userIdForChat = currentUserId;
      if (currentUserRole === 'admin') {
        userIdForChat = `admin_${currentAgencyId}`;
      } else if (currentUserRole === 'officeAdmin' || currentUserRole === 'driver') {
        // For officeAdmin and driver, ensure userId is a string (matches Flutter behavior)
        userIdForChat = String(currentUserId || '');
      }

      // CRITICAL: Verify userRole is correct
      if (!currentUserRole || (currentUserRole !== 'admin' && currentUserRole !== 'driver' && currentUserRole !== 'officeAdmin')) {
        console.error(`❌ INVALID USER ROLE: "${currentUserRole}"`);
        console.error(`❌ Must be 'admin', 'driver', or 'officeAdmin'`);
        console.error(`❌ Received from props:`, { currentUserRole, currentUserId, currentAgencyId });
      }
      
      // Flutter URL format: '$apiUrl/rooms/$userId?agencyId=$agencyId&userRole=$userRole'
      // Use actual API URL from ChatService instance or environment variable
      const chatApiUrl = (ChatService && ChatService.apiUrl) || process.env.REACT_APP_CHAT_API_URL || 'http://localhost:8001/api/chat';
      const expectedUrl = `${chatApiUrl}/rooms/${userIdForChat}?agencyId=${currentAgencyId}&userRole=${currentUserRole}`;
      
      // Verify: If user is admin, userRole MUST be 'admin'
      if (currentUserId && currentUserId.toString().startsWith('admin_') && currentUserRole !== 'admin') {
        console.error(`❌ CRITICAL ERROR: userId indicates admin (${currentUserId}) but userRole is "${currentUserRole}"`);
        console.error(`❌ This will cause API to return wrong rooms!`);
      }
      
      setApiUrlCalled(expectedUrl);
      
      // Call exactly like Flutter: getChatRooms(userId, agencyId, userRole: userRole)
      // Flutter ALWAYS passes userRole parameter
      // CRITICAL: currentUserRole MUST be the actual user role ('admin', 'driver', or 'officeAdmin')
      let rooms = await ChatService.getChatRooms(
        userIdForChat,
        currentAgencyId,
        currentUserRole // ALWAYS pass userRole (matches Flutter) - MUST be correct!
      );
      
      // Store API response for debugging
      if (window.__CHAT_API_DEBUG__) {
        setApiResponse(window.__CHAT_API_DEBUG__.lastResponse);
      }

      // Log room breakdown by participant type for officeAdmin
      if (currentUserRole === 'officeAdmin') {
        const officeAdminRooms = rooms.filter(room => {
          const participants = room.participants || [];
          return participants.some(p => p.userRole === 'officeAdmin' && String(p.userId || p.user_id) !== String(userIdForChat));
        });
        const driverRooms = rooms.filter(room => {
          const participants = room.participants || [];
          return participants.some(p => p.userRole === 'driver');
        });
        const adminRooms = rooms.filter(room => {
          const participants = room.participants || [];
          return participants.some(p => p.userRole === 'admin');
        });
      }
      
      // Log room types and participants for admin users
      if (currentUserRole === 'admin') {
        const driverRooms = rooms.filter(room => 
          room.participants?.some(p => p.userRole === 'driver')
        );
        const officeAdminRooms = rooms.filter(room => 
          room.participants?.some(p => p.userRole === 'officeAdmin')
        );
        const otherAdminRooms = rooms.filter(room => 
          room.participants?.some(p => p.userRole === 'admin')
        );
        
        
        if (driverRooms.length === 0 && rooms.length > 0) {
          console.error('❌ PROBLEM: Admin user but NO driver rooms in response!');
          console.error('❌ All rooms:', rooms.map(r => ({
            roomId: r.roomId,
            participants: r.participants?.map(p => ({
              name: p.userName,
              role: p.userRole,
              id: p.userId || p.user_id
            }))
          })));
        }
      }

      
      // Additional deduplication check: Remove rooms with same normalized participant pairs
      // This handles cases where both "1_302" and "302_admin_CT123" exist for the same participants
      // ALSO handles cases where rooms have incorrect participants (e.g., room "4_CT123" with officeAdmin instead of admin)
      const finalRooms = [];
      const seenParticipantPairs = new Set();
      
      rooms.forEach(room => {
        if (!room.participants || room.participants.length !== 2) {
          finalRooms.push(room);
          return;
        }
        
        // Normalize participants for comparison
        // Key: Normalize admin IDs to admin_{agencyId} format for comparison
        const adminFormatId = `admin_${currentAgencyId}`;
        const normalizedPair = room.participants.map(p => {
          const pUserId = String(p.userId || p.user_id || '');
          // If admin participant, normalize to admin_CT123 format
          if (p.userRole === 'admin' && !pUserId.startsWith('admin_')) {
            return adminFormatId;
          }
          return pUserId;
        }).sort().join('_');
        
        // Also create a "other participant" identifier for filtering duplicates by name
        // This handles cases where different rooms show same person (e.g., "azhra" in multiple rooms)
        const otherParticipantIds = room.participants
          .filter(p => {
            const pUserId = String(p.userId || p.user_id || '');
            // Filter out current user (normalized)
            if (currentUserRole === 'admin') {
              const normalizedUserId = p.userRole === 'admin' && !pUserId.startsWith('admin_') 
                ? adminFormatId 
                : pUserId;
              return normalizedUserId !== adminFormatId;
            } else {
              return pUserId !== String(currentUserId || '');
            }
          })
          .map(p => {
            const pUserId = String(p.userId || p.user_id || '');
            // Normalize admin IDs
            if (p.userRole === 'admin' && !pUserId.startsWith('admin_')) {
              return adminFormatId;
            }
            return pUserId;
          })
          .sort()
          .join('_');
        
        
        // Check for duplicates by normalized participant pair
        if (!seenParticipantPairs.has(normalizedPair)) {
          // Also check if we already have a room with same "other participant"
          // This handles case where admin sees "azhra" in both room "4_CT123" and "302_admin_CT123"
          // Issue: Admin is not a participant in "4_CT123" but sees it because backend returns driver_admin rooms
          // Solution: If admin sees same person in multiple rooms, prefer the room where admin IS a participant
          let isDuplicateByOtherParticipant = false;
          
          if (currentUserRole === 'admin') {
            // Check if current room has admin as participant
            const currentRoomHasAdmin = room.participants.some(p => {
              const pUserId = String(p.userId || p.user_id || '');
              return p.userRole === 'admin' && (pUserId === adminFormatId || pUserId === '1');
            });
            
            // For admin users, check if we already have a room showing the same other participant
            const existingRoomWithSameOther = finalRooms.find(r => {
              if (!r.participants || r.participants.length !== 2) return false;
              
              // Check if existing room has admin as participant
              const rHasAdmin = r.participants.some(p => {
                const pUserId = String(p.userId || p.user_id || '');
                return p.userRole === 'admin' && (pUserId === adminFormatId || pUserId === '1');
              });
              
              // Get other participant from existing room (exclude admin)
              const rOtherIds = r.participants
                .filter(p => {
                  const pUserId = String(p.userId || p.user_id || '');
                  const normalized = p.userRole === 'admin' && !pUserId.startsWith('admin_') 
                    ? adminFormatId 
                    : pUserId;
                  return normalized !== adminFormatId && normalized !== '1';
                })
                .map(p => {
                  const pUserId = String(p.userId || p.user_id || '');
                  if (p.userRole === 'admin' && !pUserId.startsWith('admin_')) {
                    return adminFormatId;
                  }
                  return pUserId;
                })
                .sort()
                .join('_');
              
              // Get other participant from current room (exclude admin)
              const currentOtherIds = room.participants
                .filter(p => {
                  const pUserId = String(p.userId || p.user_id || '');
                  const normalized = p.userRole === 'admin' && !pUserId.startsWith('admin_') 
                    ? adminFormatId 
                    : pUserId;
                  return normalized !== adminFormatId && normalized !== '1';
                })
                .map(p => {
                  const pUserId = String(p.userId || p.user_id || '');
                  if (p.userRole === 'admin' && !pUserId.startsWith('admin_')) {
                    return adminFormatId;
                  }
                  return pUserId;
                })
                .sort()
                .join('_');
              
              // If same other participant (e.g., both show "azhra" - userId 302)
              if (rOtherIds === currentOtherIds && rOtherIds !== '') {
                // CRITICAL: Prefer room where admin IS a participant
                if (currentRoomHasAdmin && !rHasAdmin) {
                  // Current room has admin, existing doesn't - replace
                  const index = finalRooms.indexOf(r);
                  finalRooms[index] = room;
                  return true; // Found and replaced
                } else if (!currentRoomHasAdmin && rHasAdmin) {
                  // Existing room has admin, current doesn't - skip current
                  isDuplicateByOtherParticipant = true;
                  return true; // Found duplicate
                } else if (room.roomId.includes('admin_') && !r.roomId.includes('admin_')) {
                  // Both have/don't have admin, prefer normalized ID format
                  const index = finalRooms.indexOf(r);
                  finalRooms[index] = room;
                  return true;
                } else {
                  // Same other participant, prefer existing (or prefer room with admin)
                  if (rHasAdmin || currentRoomHasAdmin) {
                    // At least one has admin - prefer the one with admin
                    if (currentRoomHasAdmin && !rHasAdmin) {
                      const index = finalRooms.indexOf(r);
                      finalRooms[index] = room;
                      return true;
                    } else {
                      isDuplicateByOtherParticipant = true;
                      return true;
                    }
                  } else {
                    // Neither has admin - prefer existing
                    isDuplicateByOtherParticipant = true;
                    return true;
                  }
                }
              }
              return false;
            });
            
            if (isDuplicateByOtherParticipant) {
              return; // Skip this room
            }
          }
          
          seenParticipantPairs.add(normalizedPair);
          finalRooms.push(room);
        } else {
          // Duplicate found by normalized pair - check if we should replace existing room
          const existingRoom = finalRooms.find(r => {
            if (!r.participants || r.participants.length !== 2) return false;
            const rNormalized = r.participants.map(p => {
              const pUserId = String(p.userId || p.user_id || '');
              if (p.userRole === 'admin' && !pUserId.startsWith('admin_')) {
                return adminFormatId;
              }
              return pUserId;
            }).sort().join('_');
            return rNormalized === normalizedPair;
          });
          
          if (existingRoom) {
            const currentHasNormalized = room.roomId.includes('admin_');
            const existingHasNormalized = existingRoom.roomId.includes('admin_');
            
            if (currentHasNormalized && !existingHasNormalized) {
              // Replace with normalized format room
              const index = finalRooms.indexOf(existingRoom);
              finalRooms[index] = room;
            }
          }
        }
      });
      
      
      // Use finalRooms instead of rooms
      rooms = finalRooms;
      
      // FILTER: For officeAdmin users, exclude office admin-to-office admin chats
      rooms = filterOfficeAdminToOfficeAdminRooms(rooms);
      
      // Add helper methods to all rooms
      rooms.forEach((room, index) => {
        
        // Ensure helper method exists
        if (!room.getOtherParticipant) {
          room.getOtherParticipant = (userId) => {
            if (!room.participants || !Array.isArray(room.participants)) {
              return null;
            }
            const searchUserId = String(userId || '');
            
            // Normalize admin IDs for comparison (admin_CT123 or "1" should match)
            const adminFormatId = `admin_${currentAgencyId}`;
            const isAdminUserId = (pUserId) => {
              const userIdStr = String(pUserId || '');
              // Check if it's the current user (admin_CT123 or "1" normalized)
              if (userIdStr === searchUserId) return true;
              if (searchUserId === adminFormatId && (userIdStr === '1' || userIdStr === adminFormatId)) return true;
              if (userIdStr === adminFormatId && (searchUserId === '1' || searchUserId === adminFormatId)) return true;
              return false;
            };
            
            // For admin users: prioritize finding driver or officeAdmin (not other admin)
            let found = null;
            
            if (currentUserRole === 'admin') {
              // Priority 1: Find driver (most common for admin)
              found = room.participants.find(p => {
                const pUserId = String(p.userId || p.user_id || '');
                return p.userRole === 'driver' && !isAdminUserId(pUserId);
              });
              
              // Priority 2: Find officeAdmin
              if (!found) {
                found = room.participants.find(p => {
                  const pUserId = String(p.userId || p.user_id || '');
                  return p.userRole === 'officeAdmin' && !isAdminUserId(pUserId);
                });
              }
              
              // Priority 3: Find any non-admin participant
              if (!found) {
                found = room.participants.find(p => {
                  const pUserId = String(p.userId || p.user_id || '');
                  return p.userRole !== 'admin' && !isAdminUserId(pUserId);
                });
              }
            } else if (currentUserRole === 'officeAdmin') {
              // For officeAdmin users: prioritize finding other officeAdmin, then driver, then admin
              // Priority 1: Find other officeAdmin (office admin to office admin chat)
              found = room.participants.find(p => {
                const pUserId = String(p.userId || p.user_id || '');
                return p.userRole === 'officeAdmin' && pUserId !== searchUserId && !isAdminUserId(pUserId);
              });
              
              // Priority 2: Find driver
              if (!found) {
                found = room.participants.find(p => {
                  const pUserId = String(p.userId || p.user_id || '');
                  return p.userRole === 'driver' && !isAdminUserId(pUserId);
                });
              }
              
              // Priority 3: Find admin
              if (!found) {
                found = room.participants.find(p => {
                  const pUserId = String(p.userId || p.user_id || '');
                  return p.userRole === 'admin' && !isAdminUserId(pUserId);
                });
              }
            } else {
              // For driver users: find any participant that's not the current user
              found = room.participants.find(p => {
                const pUserId = String(p.userId || p.user_id || '');
                return pUserId !== searchUserId && !isAdminUserId(pUserId);
              });
            }
            
            // Final fallback: any participant that's not current user
            if (!found && room.participants.length > 0) {
              found = room.participants.find(p => {
                const pUserId = String(p.userId || p.user_id || '');
                return !isAdminUserId(pUserId);
              }) || (room.participants.length > 1 ? room.participants.find(p => {
                const pUserId = String(p.userId || p.user_id || '');
                return String(pUserId) !== searchUserId;
              }) : null);
            }
            
            return found;
          };
        }
      });

      // Calculate total unread count
      // unreadCount can be a number (from backend) or an object {userId: count}
      let totalUnread = 0;
      rooms.forEach(room => {
        let unread = 0;
        
        // Check if unreadCount is a method
        if (typeof room.getUnreadCount === 'function') {
          unread = room.getUnreadCount(userIdForChat);
        }
        // Check if unreadCount is an object keyed by userId
        else if (room.unreadCount && typeof room.unreadCount === 'object') {
          unread = room.unreadCount[userIdForChat] || 0;
        }
        // Check if unreadCount is a number (direct count from backend)
        else if (typeof room.unreadCount === 'number') {
          // Only count if message is not from current user
          const lastMessageSender = room.lastMessageSender || room.lastMessage?.senderId;
          if (lastMessageSender && lastMessageSender !== userIdForChat) {
            unread = room.unreadCount;
          }
        }
        
        totalUnread += unread;
      });

      // CRITICAL: Filter office admin-to-office admin rooms before setting state
      const filteredRooms = filterOfficeAdminToOfficeAdminRooms(rooms);

      setChatRooms(filteredRooms);
      setTotalUnreadCount(totalUnread);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading chat rooms:', error);
      setIsLoading(false);
    }
  };

  // Keep ref updated so window.__refreshChatList always calls latest loadChatRooms
  loadChatRoomsRef.current = loadChatRooms;

  const updateChatListWithNewMessage = (message) => {
    // Only update if user is not actively scrolling to prevent jerking
    if (isUserScrollingListRef.current) {
      // Still update the data but don't move room to top while scrolling
      setChatRooms(prev => {
        const roomIndex = prev.findIndex(r => r.roomId === message.roomId);
        if (roomIndex !== -1) {
          const room = prev[roomIndex];
          const updatedRoom = {
            ...room,
            lastMessage: message.message,
            lastMessageTime: message.createdAt,
            unreadCount: {
              ...room.unreadCount,
              [currentUserId]: message.senderId !== currentUserId 
                ? (room.unreadCount?.[currentUserId] || 0) + 1
                : (room.unreadCount?.[currentUserId] || 0),
            },
          };
          // Update in place without moving to top
          const newRooms = [...prev];
          newRooms[roomIndex] = updatedRoom;
          
          // CRITICAL: Filter office admin-to-office admin rooms
          const filteredRooms = filterOfficeAdminToOfficeAdminRooms(newRooms);
          
          // Recalculate total unread
          let totalUnread = 0;
          filteredRooms.forEach(r => {
            totalUnread += r.getUnreadCount?.(currentUserId) || 
                          r.unreadCount?.[currentUserId] || 0;
          });
          setTotalUnreadCount(totalUnread);
          
          return filteredRooms;
        }
        return prev;
      });
      return;
    }
    
    // Normal update when not scrolling - move room to top
    setChatRooms(prev => {
      const roomIndex = prev.findIndex(r => r.roomId === message.roomId);
      
      if (roomIndex !== -1) {
        // Update existing room
        const room = prev[roomIndex];
        const updatedRoom = {
          ...room,
          lastMessage: message.message,
          lastMessageTime: message.createdAt,
          unreadCount: {
            ...room.unreadCount,
            [currentUserId]: message.senderId !== currentUserId 
              ? (room.unreadCount?.[currentUserId] || 0) + 1
              : (room.unreadCount?.[currentUserId] || 0),
          },
        };

        // Remove from current position and add to top
        const newRooms = [...prev];
        newRooms.splice(roomIndex, 1);
        newRooms.unshift(updatedRoom);
        
        // Filter office admin-to-office admin rooms
        const filteredRooms = filterOfficeAdminToOfficeAdminRooms(newRooms);
        
        // Recalculate total unread
        let totalUnread = 0;
        filteredRooms.forEach(r => {
          totalUnread += r.getUnreadCount?.(currentUserId) || 
                        r.unreadCount?.[currentUserId] || 0;
        });
        setTotalUnreadCount(totalUnread);

        return filteredRooms;
      } else {
        // New room - reload (which will apply filter)
        loadChatRooms();
        return prev;
      }
    });
  };

  const handleRoomUpdate = (data) => {
    // Handle different update types for multi-portal sync
    if (data.type === 'list_update' && data.rooms) {
      // Full room list update (from periodic sync or server push)
      // Filter office admin-to-office admin rooms before setting
      const filteredRooms = filterOfficeAdminToOfficeAdminRooms(data.rooms);
      setChatRooms(filteredRooms);
      
      // Recalculate total unread
      let totalUnread = 0;
      filteredRooms.forEach(r => {
        totalUnread += r.getUnreadCount?.(currentUserId) || 
                      r.unreadCount?.[currentUserId] || 0;
      });
      setTotalUnreadCount(totalUnread);
      return;
    }
    
    if (data.type === 'synced' && data.rooms) {
      // Messages synced - update room list if needed
      loadChatRooms();
      return;
    }
    
    // Single room update (existing behavior)
    setChatRooms(prev => {
      const roomIndex = prev.findIndex(r => r.roomId === data.roomId);
      
      if (roomIndex !== -1) {
        const room = prev[roomIndex];
        const updatedRoom = {
          ...room,
          lastMessage: data.lastMessage || room.lastMessage,
          lastMessageTime: data.lastMessageTime 
            ? new Date(data.lastMessageTime) 
            : room.lastMessageTime,
          unreadCount: data.unreadCount || room.unreadCount,
        };
        
        const newRooms = [...prev];
        newRooms.splice(roomIndex, 1);
        newRooms.unshift(updatedRoom);
        
        // Filter office admin-to-office admin rooms
        const filteredRooms = filterOfficeAdminToOfficeAdminRooms(newRooms);
        
        // Recalculate total unread
        let totalUnread = 0;
        filteredRooms.forEach(r => {
          totalUnread += r.getUnreadCount?.(currentUserId) || 
                        r.unreadCount?.[currentUserId] || 0;
        });
        setTotalUnreadCount(totalUnread);

        return filteredRooms;
      } else {
        loadChatRooms();
        return prev;
      }
    });
  };

  const filterChatRooms = () => {
    // CRITICAL: First filter out office admin-to-office admin rooms
    const roomsAfterOfficeAdminFilter = filterOfficeAdminToOfficeAdminRooms(chatRooms);
    
    if (searchQuery.trim() === '') {
      setFilteredChatRooms(roomsAfterOfficeAdminFilter);
      setIsSearching(false);
    } else {
      setIsSearching(true);
      const query = searchQuery.toLowerCase();
      const filtered = roomsAfterOfficeAdminFilter.filter(room => {
        // Find other participant with proper type handling using userIdForChat
        let otherParticipant = null;
        if (room.getOtherParticipant && typeof room.getOtherParticipant === 'function') {
          otherParticipant = room.getOtherParticipant(userIdForChat);
        }
        if (!otherParticipant && room.participants && Array.isArray(room.participants)) {
          const searchUserId = String(userIdForChat || '');
          otherParticipant = room.participants.find(p => {
            const pUserId = String(p.userId || p.user_id || '');
            return pUserId !== searchUserId;
          });
        }
        const participantName = otherParticipant?.userName?.toLowerCase() || '';
        const lastMessage = room.lastMessage?.toLowerCase() || '';
        return participantName.includes(query) || lastMessage.includes(query);
      });
      setFilteredChatRooms(filtered);
    }
  };

  const handleChatRoomClick = (room) => {
    if (onChatRoomClick) {
      onChatRoomClick(room);
    }
  };

  // Get current date for display
  const currentDate = new Date().toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });

  // Get agency name for display
  const agencyName = currentAgencyId ? `SUT Home Care (${currentAgencyId})` : 'CareTap';

  return (
    <div className="chat-list-screen" style={{ padding: 0, margin: 0, height: '100vh' }}>
      {/* Main Content - Only Chat List */}
      <main className="chat-main-content" style={{ padding: 0, height: '100%', maxWidth: '100%', margin: 0 }}>

        {/* Chat Container - Full Width */}
        <div className="chat-container" style={{ height: '100%', borderRadius: 0, boxShadow: 'none', border: 'none' }}>
          {/* Sidebar - Chat List */}
          <div className="chat-sidebar" style={{ width: '100%', borderRight: 'none' }}>
            {/* Sidebar Header */}
            <div className="sidebar-header">
              <div className="sidebar-title-row">
                <h3 className="sidebar-title">Messages</h3>
                <div className="sidebar-actions">
                  <button 
                    className="sidebar-icon-button" 
                    onClick={() => setShowSearchBar(!showSearchBar)}
                    title="Search"
                  >
                    <span className="material-icons-outlined">search</span>
                  </button>
                  {onCreateChat && (
                    <button 
                      className="sidebar-icon-button" 
                      onClick={onCreateChat}
                      title="New Chat"
                    >
                      <span className="material-icons-outlined">add</span>
                    </button>
                  )}
                  <button 
                    className="sidebar-icon-button" 
                    onClick={loadChatRooms}
                    title="Refresh"
                  >
                    <span className="material-icons-outlined">refresh</span>
                  </button>
                </div>
              </div>
              
              {/* Search Input - Toggleable */}
              {showSearchBar && (
                <div className="search-container" style={{ animation: 'slideDown 0.2s ease-out' }}>
                  <span className="search-icon">
                    <span className="material-icons-outlined">search</span>
                  </span>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    autoFocus
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setShowSearchBar(false);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#a8a29e',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#78716c'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#a8a29e'}
                  >
                    <span className="material-icons-outlined" style={{ fontSize: '1.125rem' }}>close</span>
                  </button>
                </div>
              )}
            </div>

            {/* Chat List */}
            <div className="chat-list-container custom-scrollbar" ref={chatListContainerRef}>
              {isLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                </div>
              ) : filteredChatRooms.length === 0 ? (
                <div className="empty-chat-list">
                  <div className="empty-title">
                    {isSearching ? 'No chats found' : 'No conversations yet'}
                  </div>
                  <div className="empty-subtitle">
                    {isSearching ? 'Try a different search term' : 'Start a new chat by tapping the + button'}
                  </div>
                </div>
              ) : (
                filteredChatRooms.map(room => {
                // Find other participant with proper type handling
                // Use userIdForChat (formatted admin ID) instead of currentUserId
                let otherParticipant = null;
                
                // Use the helper method if available
                if (room.getOtherParticipant && typeof room.getOtherParticipant === 'function') {
                  otherParticipant = room.getOtherParticipant(userIdForChat);
                  
                  // Validate: If we're admin and room has driver, we should find driver, not officeAdmin
                  if (currentUserRole === 'admin' && otherParticipant) {
                    const hasDriver = room.participants?.some(p => p.userRole === 'driver');
                    if (hasDriver && otherParticipant.userRole !== 'driver') {
                      // Room has driver but we found non-driver - find driver instead
                      const driver = room.participants.find(p => p.userRole === 'driver');
                      if (driver) {
                        otherParticipant = driver;
                      }
                    }
                  }
                }
                
                // Fallback: direct participant search with admin ID format handling
                if (!otherParticipant && room.participants && Array.isArray(room.participants)) {
                  const searchUserId = String(userIdForChat || '');
                  const adminFormatId = `admin_${currentAgencyId}`;
                  
                  // Normalize admin ID for comparison
                  const isCurrentUser = (pUserId) => {
                    const pUserIdStr = String(pUserId || '');
                    if (pUserIdStr === searchUserId) return true;
                    // Handle admin ID mapping: "1" and "admin_CT123" are same
                    if (searchUserId === adminFormatId && (pUserIdStr === '1' || pUserIdStr === adminFormatId)) return true;
                    if (pUserIdStr === adminFormatId && (searchUserId === '1' || searchUserId === adminFormatId)) return true;
                    return false;
                  };
                  
                  // Priority: For admin users, prefer driver, then officeAdmin
                  if (currentUserRole === 'admin') {
                    // First: find driver
                    otherParticipant = room.participants.find(p => {
                      const pUserId = String(p.userId || p.user_id || '');
                      return p.userRole === 'driver' && !isCurrentUser(pUserId);
                    });
                    
                    // Second: find officeAdmin (if no driver)
                    if (!otherParticipant) {
                      otherParticipant = room.participants.find(p => {
                        const pUserId = String(p.userId || p.user_id || '');
                        return p.userRole === 'officeAdmin' && !isCurrentUser(pUserId);
                      });
                    }
                  } else {
                    // For non-admin: find any participant that's not current user
                    otherParticipant = room.participants.find(p => {
                      const pUserId = String(p.userId || p.user_id || '');
                      return !isCurrentUser(pUserId);
                    });
                  }
                }
                
                // If still no participant found, use first available participant
                if (!otherParticipant && room.participants && room.participants.length > 0) {
                  const searchUserId = String(userIdForChat || '');
                  const adminFormatId = `admin_${currentAgencyId}`;
                  
                  const isCurrentUser = (pUserId) => {
                    const pUserIdStr = String(pUserId || '');
                    if (pUserIdStr === searchUserId) return true;
                    if (searchUserId === adminFormatId && (pUserIdStr === '1' || pUserIdStr === adminFormatId)) return true;
                    if (pUserIdStr === adminFormatId && (searchUserId === '1' || searchUserId === adminFormatId)) return true;
                    return false;
                  };
                  
                  otherParticipant = room.participants.find(p => {
                    const pUserId = String(p.userId || p.user_id || '');
                    return !isCurrentUser(pUserId);
                  }) || room.participants[0];
                  
                  if (!otherParticipant) {
                    console.warn('⚠️ Could not find other participant for room:', room.roomId, {
                      userIdForChat,
                      currentUserId,
                      currentUserRole,
                      participants: room.participants
                    });
                    otherParticipant = room.participants[0];
                  }
                }
                
                // Check online status - handle both string and number userIds (matches Flutter)
                const otherUserId = otherParticipant?.userId || otherParticipant?.user_id;
                let isOnline = false;
                if (otherUserId) {
                  const userIdStr = String(otherUserId);
                  // Check if userId exists in Set (handles type mismatches)
                  onlineUsers.forEach(id => {
                    if (String(id) === userIdStr) {
                      isOnline = true;
                    }
                  });
                }
                if (!room.getUnreadCount) {
                  room.getUnreadCount = (userId) => {
                    // Handle both number and object formats
                    if (typeof room.unreadCount === 'number') {
                      const lastMessageSender = room.lastMessageSender || room.lastMessage?.senderId;
                      return (lastMessageSender && lastMessageSender !== userId) ? room.unreadCount : 0;
                    } else if (room.unreadCount && typeof room.unreadCount === 'object') {
                      return room.unreadCount[userId] || 0;
                    }
                    return 0;
                  };
                }

                  return (
                    <ChatRoomListItem
                      key={room.roomId}
                      room={room}
                      currentUserId={userIdForChat}
                      currentUserRole={currentUserRole}
                      onTap={() => handleChatRoomClick(room)}
                      isOnline={isOnline}
                    />
                  );
                })
              )}
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default ChatListScreen;

